import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, X, Mail, Target, Users, Sparkles, Copy, Check,
  Save, RefreshCw, ChevronDown, ChevronUp, Loader2,
  BookOpen, Lightbulb, ArrowRight, Star
} from 'lucide-react';
import { useChat } from '../../../hooks/useChat';
import toast from 'react-hot-toast';
import { templateService } from '../../../services/templateService';

const CAMPAIGN_GOALS = [
  { value: 'lead_generation', label: 'Lead Generation', desc: 'Attract new prospects' },
  { value: 'product_launch', label: 'Product Launch', desc: 'Announce new offerings' },
  { value: 'nurture', label: 'Lead Nurturing', desc: 'Build relationships over time' },
  { value: 'reengagement', label: 'Re-engagement', desc: 'Win back inactive contacts' },
  { value: 'upsell', label: 'Upsell / Cross-sell', desc: 'Expand existing accounts' },
  { value: 'event_invite', label: 'Event Invitation', desc: 'Drive event registrations' },
  { value: 'promotional', label: 'Promotional Offer', desc: 'Drive conversions with deals' },
  { value: 'onboarding', label: 'Onboarding', desc: 'Welcome and activate new users' },
  { value: 'follow_up', label: 'Follow-up', desc: 'Continue a conversation' },
  { value: 'cold_outreach', label: 'Cold Outreach', desc: 'First contact with prospects' }
];

const AUDIENCE_TYPES = [
  'B2B Decision Makers', 'Small Business Owners', 'Enterprise Executives',
  'Marketing Professionals', 'Sales Teams', 'IT Professionals',
  'Startup Founders', 'E-commerce Shoppers', 'Healthcare Professionals',
  'Real Estate Investors', 'Financial Advisors', 'HR Managers',
  'Existing Customers', 'Trial Users', 'Inactive Subscribers'
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'casual', label: 'Casual' },
  { value: 'authoritative', label: 'Authoritative' },
  { value: 'empathetic', label: 'Empathetic' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
  { value: 'consultative', label: 'Consultative' }
];

const INDUSTRIES = [
  'Technology', 'SaaS / Software', 'Healthcare', 'Finance', 'Real Estate',
  'E-commerce', 'Education', 'Marketing Agency', 'Consulting', 'Legal',
  'Manufacturing', 'Retail', 'Non-profit', 'Automotive', 'Other'
];

const parseGeneratedContent = (rawText) => {
  try {
    const jsonMatch = rawText?.match(/```json\n?([\s\S]*?)\n?```/) ||
      rawText?.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const jsonStr = jsonMatch?.[1] || jsonMatch?.[0];
      return JSON.parse(jsonStr);
    }
    return null;
  } catch {
    return null;
  }
};

const EmailTemplateGenerator = ({ onClose, onSaved }) => {
  const [step, setStep] = useState(1); // 1: Configure, 2: Generated, 3: Customize
  const [formData, setFormData] = useState({
    campaignGoal: '',
    audience: '',
    customAudience: '',
    tone: 'professional',
    industry: '',
    productService: '',
    keyBenefit: '',
    ctaAction: '',
    additionalContext: ''
  });

  const [generated, setGenerated] = useState(null);
  const [variations, setVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generatingVariations, setGeneratingVariations] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (error) toast?.error(error?.message || 'AI generation failed');
  }, [error]);

  // Parse response when it arrives
  useEffect(() => {
    if (response && !isLoading) {
      const parsed = parseGeneratedContent(response);
      if (parsed) {
        setGenerated(parsed);
        setVariations([parsed]);
        setSelectedVariation(0);
        setStep(2);
        if (parsed?.templateName) setTemplateName(parsed?.templateName);
      } else {
        toast?.error('Could not parse AI response. Please try again.');
      }
    }
  }, [response, isLoading]);

  const buildPrompt = useCallback(() => {
    const goal = CAMPAIGN_GOALS?.find(g => g?.value === formData?.campaignGoal);
    const audience = formData?.customAudience || formData?.audience;

    return [
      `You are an expert email copywriter. Generate a complete email template for a marketing campaign.`,
      ``,
      `Campaign Details:`,
      `- Goal: ${goal?.label || formData?.campaignGoal} (${goal?.desc || ''})`,
      `- Target Audience: ${audience}`,
      `- Tone: ${formData?.tone}`,
      `- Industry: ${formData?.industry || 'General'}`,
      formData?.productService ? `- Product/Service: ${formData?.productService}` : '',
      formData?.keyBenefit ? `- Key Benefit/Value Prop: ${formData?.keyBenefit}` : '',
      formData?.ctaAction ? `- Desired CTA Action: ${formData?.ctaAction}` : '',
      formData?.additionalContext ? `- Additional Context: ${formData?.additionalContext}` : '',
      ``,
      `Generate a complete email with the following structure. Respond ONLY with valid JSON:`,
      `{`,
      `  "templateName": "descriptive template name",`,
      `  "subjectLine": "compelling email subject line",`,
      `  "previewText": "email preview/preheader text (50-90 chars)",`,
      `  "greeting": "personalized greeting line",`,
      `  "bodyOpening": "opening paragraph that hooks the reader",`,
      `  "bodyMain": "main body copy with key value proposition (2-3 paragraphs)",`,
      `  "callToAction": "specific, action-oriented CTA button text",`,
      `  "ctaContext": "sentence before the CTA button",`,
      `  "closing": "professional closing line",`,
      `  "subjectLineAlternatives": ["alternative subject 1", "alternative subject 2", "alternative subject 3"],`,
      `  "ctaAlternatives": ["alternative CTA 1", "alternative CTA 2"],`,
      `  "personalizationTips": ["tip 1", "tip 2"],`,
      `  "estimatedOpenRate": "X-Y%",`,
      `  "bestSendTime": "recommended send time"`,
      `}`
    ]?.filter(Boolean)?.join('\n');
  }, [formData]);

  const handleGenerate = () => {
    if (!formData?.campaignGoal) { toast?.error('Please select a campaign goal'); return; }
    if (!formData?.audience && !formData?.customAudience) { toast?.error('Please specify your target audience'); return; }

    const prompt = buildPrompt();
    sendMessage([
      { role: 'system', content: 'You are an expert email copywriter and marketing strategist. Always respond with valid JSON only, no markdown outside the JSON.' },
      { role: 'user', content: prompt }
    ], { max_completion_tokens: 2000 });
  };

  const handleRegenerateVariations = async () => {
    if (!generated) return;
    setGeneratingVariations(true);
    try {
      const variationPrompt = `${buildPrompt()}\n\nIMPORTANT: Generate a DIFFERENT variation with a completely different angle, hook, and approach than the previous version. Keep the same goal but use a fresh perspective.`;
      sendMessage([
        { role: 'system', content: 'You are an expert email copywriter. Always respond with valid JSON only.' },
        { role: 'user', content: variationPrompt }
      ], { max_completion_tokens: 2000 });
    } finally {
      setGeneratingVariations(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard?.writeText(text)?.then(() => {
      setCopiedField(field);
      toast?.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName?.trim()) { toast?.error('Please enter a template name'); return; }
    const current = variations?.[selectedVariation] || generated;
    if (!current) return;

    setSaving(true);
    try {
      const fullContent = [
        current?.greeting,
        '',
        current?.bodyOpening,
        '',
        current?.bodyMain,
        '',
        current?.ctaContext,
        '',
        `[${current?.callToAction}]`,
        '',
        current?.closing
      ]?.join('\n');

      await templateService?.createTemplate({
        name: templateName?.trim(),
        type: 'email',
        category: 'email_campaigns',
        subject: current?.subjectLine,
        content: fullContent,
        tags: ['ai-generated', 'email', formData?.campaignGoal, formData?.tone]?.filter(Boolean),
        target_audience: formData?.customAudience || formData?.audience,
        industry: formData?.industry || null,
        tone: formData?.tone,
        ai_generated: true,
        metadata: {
          previewText: current?.previewText,
          callToAction: current?.callToAction,
          subjectLineAlternatives: current?.subjectLineAlternatives,
          ctaAlternatives: current?.ctaAlternatives,
          campaignGoal: formData?.campaignGoal,
          estimatedOpenRate: current?.estimatedOpenRate,
          bestSendTime: current?.bestSendTime
        }
      });

      toast?.success('Template saved to library!');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Save error:', err);
      toast?.error('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const current = variations?.[selectedVariation] || generated;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
      onClick={(e) => e?.target === e?.currentTarget && onClose?.()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">AI Email Template Studio</h2>
              <p className="text-xs text-gray-500">Generate subject lines, body copy & CTAs with OpenAI</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Step indicator */}
            <div className="hidden sm:flex items-center gap-1 mr-4">
              {[1, 2]?.map(s => (
                <div key={s} className={`flex items-center gap-1`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step >= s ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>{s}</div>
                  {s < 2 && <div className={`w-8 h-0.5 ${step > s ? 'bg-purple-600' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* STEP 1: Configuration */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-6 space-y-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Campaign Goal */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      <Target className="inline h-4 w-4 mr-1 text-purple-600" />
                      Campaign Goal <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {CAMPAIGN_GOALS?.map(goal => (
                        <button
                          key={goal?.value}
                          onClick={() => setFormData(p => ({ ...p, campaignGoal: goal?.value }))}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            formData?.campaignGoal === goal?.value
                              ? 'border-purple-500 bg-purple-50 text-purple-700' :'border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                        >
                          <div className="text-xs font-semibold">{goal?.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5 leading-tight">{goal?.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Target Audience */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Users className="inline h-4 w-4 mr-1 text-blue-600" />
                      Target Audience <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData?.audience}
                      onChange={e => setFormData(p => ({ ...p, audience: e?.target?.value, customAudience: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    >
                      <option value="">Select audience type...</option>
                      {AUDIENCE_TYPES?.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input
                      type="text"
                      value={formData?.customAudience}
                      onChange={e => setFormData(p => ({ ...p, customAudience: e?.target?.value, audience: '' }))}
                      placeholder="Or describe your audience (e.g., 'CFOs at mid-market SaaS companies')"
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>

                  {/* Tone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Tone</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TONES?.map(t => (
                        <button
                          key={t?.value}
                          onClick={() => setFormData(p => ({ ...p, tone: t?.value }))}
                          className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                            formData?.tone === t?.value
                              ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          {t?.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Industry */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                    <select
                      value={formData?.industry}
                      onChange={e => setFormData(p => ({ ...p, industry: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    >
                      <option value="">Select industry...</option>
                      {INDUSTRIES?.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>

                  {/* Product/Service */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Product / Service</label>
                    <input
                      type="text"
                      value={formData?.productService}
                      onChange={e => setFormData(p => ({ ...p, productService: e?.target?.value }))}
                      placeholder="e.g., 'AI-powered CRM software'"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>

                  {/* Advanced Options Toggle */}
                  <div className="lg:col-span-2">
                    <button
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      Advanced Options
                    </button>

                    <AnimatePresence>
                      {showAdvanced && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Key Benefit / Value Prop</label>
                              <input
                                type="text"
                                value={formData?.keyBenefit}
                                onChange={e => setFormData(p => ({ ...p, keyBenefit: e?.target?.value }))}
                                placeholder="e.g., 'Save 10 hours/week on lead follow-up'"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Desired CTA Action</label>
                              <input
                                type="text"
                                value={formData?.ctaAction}
                                onChange={e => setFormData(p => ({ ...p, ctaAction: e?.target?.value }))}
                                placeholder="e.g., 'Book a demo', 'Start free trial'"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Context</label>
                              <textarea
                                value={formData?.additionalContext}
                                onChange={e => setFormData(p => ({ ...p, additionalContext: e?.target?.value }))}
                                placeholder="Any specific details, pain points, or context to include..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Generated Results */}
            {step === 2 && current && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 space-y-5"
              >
                {/* Template Name */}
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
                  <Star className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Template Name</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={e => setTemplateName(e?.target?.value)}
                      placeholder="Enter a name for this template..."
                      className="w-full bg-transparent text-sm font-medium text-gray-900 focus:outline-none placeholder-gray-400"
                    />
                  </div>
                </div>

                {/* Subject Line */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-700">Subject Line</span>
                    </div>
                    <button
                      onClick={() => handleCopy(current?.subjectLine, 'subject')}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      {copiedField === 'subject' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                      Copy
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="text-base font-semibold text-gray-900">{current?.subjectLine}</p>
                    {current?.previewText && (
                      <p className="text-sm text-gray-500 mt-1">Preview: {current?.previewText}</p>
                    )}
                  </div>
                  {/* Subject Alternatives */}
                  {current?.subjectLineAlternatives?.length > 0 && (
                    <div className="px-4 pb-4">
                      <p className="text-xs font-semibold text-gray-500 mb-2">A/B Test Alternatives:</p>
                      <div className="space-y-1">
                        {current?.subjectLineAlternatives?.map((alt, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <span className="text-xs text-gray-700">{alt}</span>
                            <button onClick={() => handleCopy(alt, `alt-subject-${i}`)} className="ml-2 flex-shrink-0">
                              {copiedField === `alt-subject-${i}` ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-gray-400" />}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Body Copy */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-semibold text-gray-700">Email Body Copy</span>
                    </div>
                    <button
                      onClick={() => handleCopy(
                        [current?.greeting, '', current?.bodyOpening, '', current?.bodyMain, '', current?.ctaContext]?.join('\n'),
                        'body'
                      )}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      {copiedField === 'body' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                      Copy All
                    </button>
                  </div>
                  <div className="p-4 space-y-3">
                    {current?.greeting && (
                      <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Greeting</span>
                        <p className="text-sm text-gray-800 mt-1">{current?.greeting}</p>
                      </div>
                    )}
                    {current?.bodyOpening && (
                      <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Opening Hook</span>
                        <p className="text-sm text-gray-800 mt-1">{current?.bodyOpening}</p>
                      </div>
                    )}
                    {current?.bodyMain && (
                      <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Main Body</span>
                        <p className="text-sm text-gray-800 mt-1 whitespace-pre-line">{current?.bodyMain}</p>
                      </div>
                    )}
                    {current?.closing && (
                      <div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Closing</span>
                        <p className="text-sm text-gray-800 mt-1">{current?.closing}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-semibold text-gray-700">Call-to-Action</span>
                    </div>
                    <button
                      onClick={() => handleCopy(current?.callToAction, 'cta')}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      {copiedField === 'cta' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                      Copy
                    </button>
                  </div>
                  <div className="p-4">
                    {current?.ctaContext && (
                      <p className="text-sm text-gray-600 mb-3">{current?.ctaContext}</p>
                    )}
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold text-sm">
                      {current?.callToAction}
                      <ArrowRight className="h-4 w-4" />
                    </div>
                    {/* CTA Alternatives */}
                    {current?.ctaAlternatives?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 mb-2">CTA Alternatives:</p>
                        <div className="flex flex-wrap gap-2">
                          {current?.ctaAlternatives?.map((alt, i) => (
                            <button
                              key={i}
                              onClick={() => handleCopy(alt, `alt-cta-${i}`)}
                              className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {alt}
                              {copiedField === `alt-cta-${i}` ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-gray-400" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Insights */}
                {(current?.personalizationTips?.length > 0 || current?.estimatedOpenRate || current?.bestSendTime) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-800">AI Insights</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {current?.estimatedOpenRate && (
                        <div className="text-xs text-amber-700">
                          <span className="font-semibold">Est. Open Rate:</span> {current?.estimatedOpenRate}
                        </div>
                      )}
                      {current?.bestSendTime && (
                        <div className="text-xs text-amber-700">
                          <span className="font-semibold">Best Send Time:</span> {current?.bestSendTime}
                        </div>
                      )}
                    </div>
                    {current?.personalizationTips?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-amber-700 mb-1">Personalization Tips:</p>
                        <ul className="space-y-1">
                          {current?.personalizationTips?.map((tip, i) => (
                            <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
                              <span className="mt-0.5">•</span> {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="relative mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Your Email Template</h3>
              <p className="text-sm text-gray-500 text-center max-w-sm">
                OpenAI is crafting your subject line, body copy, and call-to-action based on your campaign details...
              </p>
              <div className="mt-4 flex gap-2">
                {['Analyzing goal...', 'Writing copy...', 'Optimizing CTA...']?.map((s, i) => (
                  <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}>{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {step === 1 ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
              <button
                onClick={handleGenerate}
                disabled={isLoading || !formData?.campaignGoal || (!formData?.audience && !formData?.customAudience)}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-sm hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Zap className="h-4 w-4" />
                Generate with AI
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={handleRegenerateVariations}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-purple-600 hover:text-purple-700 border border-purple-300 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Regenerate
                </button>
              </div>
              <button
                onClick={handleSaveTemplate}
                disabled={saving || !templateName?.trim()}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold text-sm hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save to Library
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmailTemplateGenerator;
